import React, { ReactElement, useEffect } from 'react';
import { useParams } from 'react-router';
import './confirmation.scss';

import ReservationCard from 'components/reservations/reservation-card';
import PageLoader from 'components/page-loader/page-loader';

import { useTranslate } from 'hooks/translate';
import { useQuery } from 'react-query';
import { useAuth } from 'contexts/auth-context';
import useNav from 'hooks/nav';

import { reservationsApi } from 'api/bestpark/reservations';
import { parksApi } from 'api/bestpark/parks';
import { LOGIN_PATH, RESERVATION_DETAIL_PATH } from 'constants/paths';

import MainLayout from 'layouts/main-layout/main-layout';
import TagManager from 'react-gtm-module';

interface ConfirmationPathParams {
  id: string;
}

export default function Confirmation(): ReactElement {
  const { t, locale } = useTranslate();
  const params = useParams<ConfirmationPathParams>();
  const { goTo } = useNav();
  const { authState } = useAuth();

  const reservationDetailsData = useQuery(
    ['reservation', params.id],
    reservationsApi.getReservation,
    {
      enabled: authState,
    },
  );

  // Pass purchase event to Google Tag Manager
  // via dataLayer for ecommerce tracking.
  useEffect(() => {
    if (reservationDetailsData.data) {
      const { settledAmount, reservationDays } = reservationDetailsData.data;
      TagManager.dataLayer({
        dataLayer: {
          event: 'purchase',
          category: 'reservation',
          settledAmount: settledAmount,
          quantity: reservationDays.length,
        },
      });
    }
  }, [reservationDetailsData]);

  const parksData = useQuery(['parks', locale], parksApi.getParks);

  const goToLogin = (): void => {
    goTo(LOGIN_PATH); // TODO temp email
  };

  const renderMessage = (): ReactElement => {
    if (!authState) {
      return (
        <>
          {t('reservation.payment.paymentProcessed')}
          <button onClick={goToLogin}>
            {t('reservation.registration.loginNote')}
          </button>
        </>
      );
    } else if (reservationDetailsData.isError) {
      return <></>;
    } else {
      return <>{t('reservation.payment.paymentProcessed')}</>;
    }
  };

  const renderContent = (): ReactElement => {
    if (parksData.isLoading || reservationDetailsData.isLoading) {
      return <PageLoader />;
    }

    return (
      <div className='reservation-confirmation'>
        <h1 className='confirmation-title'>
          {reservationDetailsData.isError
            ? t('reservation.confirmation.not_found')
            : t('reservation.confirmation.confirmed')}
        </h1>
        <p className='confirmation-message'>{renderMessage()}</p>
        <div className='my-reservation'>
          {reservationDetailsData.data && parksData.data && (
            <ReservationCard
              reservation={reservationDetailsData.data}
              key={reservationDetailsData.data.id}
              parks={parksData.data}
              buttonText={t('reservation.card.goToReservation')}
              buttonAction={() => {
                goTo(
                  `${RESERVATION_DETAIL_PATH}/${reservationDetailsData.data?.id}`,
                );
              }}
              href={`${RESERVATION_DETAIL_PATH}/${reservationDetailsData.data?.id}`}
            />
          )}
        </div>
      </div>
    );
  };

  return <MainLayout>{renderContent()}</MainLayout>;
}
